/**
 * ORNEXA — Universal Credit System Client Service
 * Single wallet pool per firm. AI vs WhatsApp use different rate-card costs per unit.
 */

import { dataProvider as supabase } from "@/lib/providers/data-provider";
import { create } from "zustand";

export interface CreditLedgerEntry {
  id: string;
  entry_type:
    | "plan_grant"
    | "purchase"
    | "promo"
    | "deduction_ai"
    | "deduction_wa"
    | "adjustment"
    | "refund";
  service_type: string;
  units: number;
  amount_credits: number;
  balance_after_credits: number;
  description: string;
  created_at: string;
}

export interface TenantWalletData {
  firm_id: string;
  balance_credits: number;
  plan_credits_monthly: number;
  purchased_credits: number;
  promo_credits: number;
  low_balance_threshold: number;
  is_low_balance: boolean;
  usage: {
    total_deducted: number;
    ai_deducted: number;
    wa_deducted: number;
    recent_entries: CreditLedgerEntry[];
  };
  updated_at: string;
}

/** Resolve the active firm id for wallet reads (must match payment grant p_firm_id). */
export async function resolveWalletFirmId(): Promise<string | undefined> {
  try {
    const { useTenantContext } = await import("@/lib/identity/tenant-context-store");
    const orgId = useTenantContext.getState().activeOrganizationId;
    if (orgId) return orgId;
  } catch {
    // tenant context not ready
  }
  const { data, error } = await (supabase as any).rpc("my_firm_id");
  if (!error && data) return String(data);
  const { data: sessionData } = await supabase.auth.getSession();
  const authId = sessionData.session?.user?.id;
  if (!authId) return undefined;
  const { data: profile } = await supabase
    .from("user_profiles" as never)
    .select("firm_id")
    .eq("auth_id", authId)
    .maybeSingle();
  const row = profile as { firm_id?: string } | null;
  return row?.firm_id ?? undefined;
}

interface CreditState {
  wallet: TenantWalletData | null;
  loading: boolean;
  error: string | null;
  fetchWallet: (firmId?: string) => Promise<TenantWalletData | null>;
  applyBalance: (newBalance: number, firmId?: string) => void;
  deductCredits: (
    serviceCode: string,
    units?: number,
    referenceId?: string,
    description?: string,
    metadata?: Record<string, unknown>,
  ) => Promise<{ success: boolean; newBalance?: number; error?: string }>;
  grantCredits: (
    amount: number,
    entryType?: string,
    description?: string,
    metadata?: Record<string, unknown>,
  ) => Promise<{ success: boolean; newBalance?: number; error?: string }>;
}

export const useCreditStore = create<CreditState>((set, get) => ({
  wallet: null,
  loading: false,
  error: null,

  applyBalance: (newBalance: number, firmId?: string) => {
    const current = get().wallet;
    if (!current) return;
    set({
      wallet: {
        ...current,
        firm_id: firmId ?? current.firm_id,
        balance_credits: newBalance,
        is_low_balance: newBalance <= current.low_balance_threshold,
        updated_at: new Date().toISOString(),
      },
    });
  },

  fetchWallet: async (firmId?: string) => {
    set({ loading: true, error: null });
    const resolvedFirmId = firmId ?? (await resolveWalletFirmId());
    if (!resolvedFirmId) {
      set({
        loading: false,
        error: "No active business selected. Switch to your firm and try again.",
      });
      return null;
    }

    try {
      const { data, error } = await (supabase as any).rpc("get_tenant_credit_wallet", {
        p_firm_id: resolvedFirmId,
      });
      if (!error && data) {
        const wallet = data as TenantWalletData;
        set({ wallet, loading: false, error: null });
        return wallet;
      }
    } catch {
      // Fall through to firm-scoped direct read
    }

    try {
      const { data: wData, error: wErr } = await (supabase as any)
        .from("tenant_credit_wallets")
        .select("*")
        .eq("firm_id", resolvedFirmId)
        .maybeSingle();

      if (wErr || !wData) {
        set({
          loading: false,
          error: "Credit wallet unavailable. Contact support if this persists after payment.",
        });
        return null;
      }

      const { data: lData } = await (supabase as any)
        .from("credit_ledger")
        .select("*")
        .eq("firm_id", resolvedFirmId)
        .order("created_at", { ascending: false })
        .limit(20);

      const balance = Number(wData.balance_credits ?? 0);
      const lowThreshold = Number(wData.low_balance_threshold ?? 100);
      const recentEntries = (lData ?? []).map((l: Record<string, unknown>) => ({
        id: String(l.id),
        entry_type: l.entry_type as CreditLedgerEntry["entry_type"],
        service_type: String(l.service_type ?? ""),
        units: Number(l.units ?? 1),
        amount_credits: Number(l.amount_credits ?? 0),
        balance_after_credits: Number(l.balance_after_credits ?? balance),
        description: String(l.description ?? ""),
        created_at: String(l.created_at ?? new Date().toISOString()),
      }));

      const aiDeducted = recentEntries
        .filter(
          (e: CreditLedgerEntry) =>
            e.entry_type === "deduction_ai" || e.service_type.startsWith("ai"),
        )
        .reduce((sum: number, e: CreditLedgerEntry) => sum + Math.abs(e.amount_credits), 0);

      const waDeducted = recentEntries
        .filter(
          (e: CreditLedgerEntry) =>
            e.entry_type === "deduction_wa" || e.service_type.startsWith("wa"),
        )
        .reduce((sum: number, e: CreditLedgerEntry) => sum + Math.abs(e.amount_credits), 0);

      const totalDeducted = recentEntries
        .filter((e: CreditLedgerEntry) => e.amount_credits < 0)
        .reduce((sum: number, e: CreditLedgerEntry) => sum + Math.abs(e.amount_credits), 0);

      const wallet: TenantWalletData = {
        firm_id: String(wData.firm_id ?? resolvedFirmId),
        balance_credits: balance,
        plan_credits_monthly: Number(wData.plan_credits_monthly ?? 0),
        purchased_credits: Number(wData.purchased_credits ?? 0),
        promo_credits: Number(wData.promo_credits ?? 0),
        low_balance_threshold: lowThreshold,
        is_low_balance: balance <= lowThreshold,
        usage: {
          total_deducted: totalDeducted,
          ai_deducted: aiDeducted,
          wa_deducted: waDeducted,
          recent_entries: recentEntries,
        },
        updated_at: String(wData.updated_at ?? new Date().toISOString()),
      };

      set({ wallet, loading: false, error: null });
      return wallet;
    } catch {
      set({
        loading: false,
        error: "Could not load credit wallet. Refresh or try again shortly.",
      });
      return null;
    }
  },

  deductCredits: async (serviceCode, units = 1, referenceId, description, metadata = {}) => {
    try {
      const firmId = await resolveWalletFirmId();
      const { data, error } = await (supabase as any).rpc("deduct_tenant_credits", {
        p_service_code: serviceCode,
        p_units: units,
        p_reference_id: referenceId || null,
        p_description: description || null,
        p_metadata: metadata,
        p_firm_id: firmId || null,
      });
      if (error) throw error;

      if (data?.success) {
        const current = get().wallet;
        if (current) {
          set({
            wallet: {
              ...current,
              balance_credits: data.new_balance,
              is_low_balance: data.new_balance <= current.low_balance_threshold,
            },
          });
        }
        return { success: true, newBalance: data.new_balance };
      }
      return { success: false, error: data?.error || "Insufficient credits" };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Credit deduction failed";
      return { success: false, error: message };
    }
  },

  grantCredits: async (amount, entryType = "purchase", description, metadata = {}) => {
    try {
      const firmId = await resolveWalletFirmId();
      const { data, error } = await (supabase as any).rpc("grant_tenant_credits", {
        p_credit_amount: amount,
        p_entry_type: entryType,
        p_description: description || null,
        p_metadata: metadata,
        p_firm_id: firmId || null,
      });
      if (error) throw error;

      if (data?.success) {
        await get().fetchWallet(firmId);
        return { success: true, newBalance: data.new_balance };
      }
      return { success: false, error: "Credit grant failed" };
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : "Credit top-up failed";
      return { success: false, error: message };
    }
  },
}));
